import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Copy text to the clipboard. Prefers the async Clipboard API and falls back to
 * a legacy hidden-textarea `document.execCommand('copy')` for non-secure
 * contexts / older browsers. Resolves `true` on success, `false` otherwise.
 */
export async function copyToClipboard(value: string): Promise<boolean> {
  const text = value ?? ''
  if (!text) return false

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // Fall through to the legacy path (e.g. permissions / insecure context).
    }
  }

  if (typeof document === 'undefined') return false
  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    // Keep it out of view and non-disruptive to scroll/focus.
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.top = '-9999px'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(textarea)
    return ok
  } catch {
    return false
  }
}

/**
 * Small hook for "click to copy" UX: exposes a `copied` flag that flips true on
 * a successful copy and reverts after `resetMs`, plus a `copy(value)` action.
 * The revert timer is cleaned up on unmount / re-copy so it never fires after
 * the component is gone.
 */
export function useCopyToClipboard(resetMs = 1500): {
  copied: boolean
  copy: (value: string) => Promise<boolean>
} {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    []
  )

  const copy = useCallback(
    async (value: string) => {
      const ok = await copyToClipboard(value)
      if (ok) {
        setCopied(true)
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => setCopied(false), resetMs)
      }
      return ok
    },
    [resetMs]
  )

  return { copied, copy }
}
